import { Grid, Typography, Box, Chip, Button, useTheme, alpha } from '@mui/material';
import React, { useMemo } from 'react';
import { BoxPropsType } from '../../Type';
import { createShipmentContextType } from '../../../../../types/shipment';
import CreateShipmentContext from '../../../../../context/CreateShipment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { customWeightRounding } from '../../../../../utils/convertWeight';

interface BoxDetailsPropsType extends BoxPropsType {
  setCopyBox: (copy: boolean) => void;
  isSelected: boolean;
  writePermission?: boolean;
  index: number;
}

function BoxDetails({ index, handleDeleteBox, boxData, isSelected, setCopyBox, writePermission }: BoxDetailsPropsType) {
  const theme = useTheme();
  const { shipmentType, boxes, removeFirstBoxDeleteOption } = React.useContext(
    CreateShipmentContext,
  ) as createShipmentContextType;

  const { chareableWeight } = useSelector((state: RootStateType) => state.getRates);

  const { id = '0' } = boxData;
  const weight = typeof boxData.weight === 'string' ? parseFloat(boxData.weight) : boxData.weight;

  const { unit, type } = shipmentType;

  const selectedUnit = useMemo(() => unit.split('-')[0], [unit]);

  return (
    <Box sx={{ width: '100%', p: 1.5 }}>
      {/* Box Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: isSelected ? 'primary.main' : 'text.primary' }}>
          {`Box ${parseInt(id) + 1} of ${boxes.length}`}
        </Typography>
        {isSelected && (
          <Chip
            label="Active"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}
          />
        )}
      </Box>

      {/* Metrics Row */}
      <Box sx={{ bgcolor: alpha(theme.palette.background.default, 0.5), p: 1, borderRadius: '8px', mb: 1.25 }}>
        <BoxInfo label="Actual Weight" value={`${weight || 0} ${selectedUnit}`} />
        <BoxInfo
          label="Chargeable Wt"
          value={`${
            chareableWeight?.boxWiseArr
              ? `${
                  selectedUnit === 'lb'
                    ? customWeightRounding((chareableWeight?.boxWiseArr[index]?.minChargeableWeight ?? 0) * 2.20462)
                    : (Number(chareableWeight?.boxWiseArr[index]?.minChargeableWeight) ?? 0).toFixed(2)
                } - ${
                  selectedUnit === 'lb'
                    ? customWeightRounding((chareableWeight?.boxWiseArr[index]?.maxChargeableWeight ?? 0) * 2.20462)
                    : (Number(chareableWeight?.boxWiseArr[index]?.maxChargeableWeight) ?? 0).toFixed(2)
                }`
              : (0).toFixed(2)
          } ${selectedUnit}`}
        />
        <BoxInfo
          label="Volumetric Wt"
          value={`${
            chareableWeight?.boxWiseArr && chareableWeight?.boxWiseArr[index]?.maxVolumetricWeight
              ? selectedUnit === 'lb'
                ? (chareableWeight.boxWiseArr[index].maxVolumetricWeight * 2.20462).toFixed(2)
                : chareableWeight.boxWiseArr[index].maxVolumetricWeight.toFixed(2)
              : (0).toFixed(2)
          } ${selectedUnit}`}
        />
      </Box>

      {/* Action Buttons */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          {id === '0' && removeFirstBoxDeleteOption ? (
            <></>
          ) : (
            <Button
              size="small"
              fullWidth
              variant="outlined"
              color="error"
              onClick={e => {
                e.stopPropagation();
                handleDeleteBox?.();
              }}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              disabled={!writePermission ? !writePermission : boxes.length < 2 || type !== 'parcel'}
              sx={{
                fontSize: '0.75rem',
                py: 0.25,
                borderRadius: '6px',
                textTransform: 'none',
              }}
            >
              Delete
            </Button>
          )}
        </Grid>
        <Grid item xs={6}>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            onClick={e => {
              e.stopPropagation();
              setCopyBox(true);
            }}
            startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
            disabled={!writePermission ? !writePermission : type !== 'parcel'}
            sx={{
              fontSize: '0.75rem',
              py: 0.25,
              borderRadius: '6px',
              textTransform: 'none',
            }}
          >
            Duplicate
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

function BoxInfo({ value, label }: { label: string; value: string | number }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        {label}:
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.75rem' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default BoxDetails;
